<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import { AttendStatus, ClassStatus, ClassStudentInfo } from '../../types/class';
import { TEXT } from '../../configs/text';
import checkIcon from '../../assets/icons/check.svg'
import { useClassStore } from '../../stores';
import { Session, sessionMap } from '../../types/session';
import { computed, ref } from 'vue';

const classStore = useClassStore()
const { t } = useI18n()
enum Order {
    none = 'none',
    asc = 'asc',
    desc = 'desc'
}
const sortOrder = ref<Order>(Order.none);
const sortingColumn = ref<keyof ClassStudentInfo | null>(null);
const sortClickCount = ref(0);
const loggedInStudents = computed<ClassStudentInfo[]>(() => {
    return classStore.selectedClassDetails?.students?.filter((student) => student.status == AttendStatus.attended) || []
})
const students = computed<ClassStudentInfo[]>(() => {
    let studentData = classStore.selectedClassDetails?.students || [];
    if (classStore.selectedClassDetails?.state == ClassStatus.on_going) {
        studentData = loggedInStudents.value
    }
    if (sortOrder.value === Order.none) {
        sortClickCount.value = 0;
        return studentData;
    } else {
        const sortingStudents = ref<ClassStudentInfo[]>(JSON.parse(JSON.stringify(studentData)) || []);
        return sortingStudents.value.sort((a, b) => {
            if (!sortingColumn.value) return 0;

            if (sortingColumn.value === 'full_name') {
                const lastNameA = (a.full_name?.split(' ').pop() ?? '').trim();
                const lastNameB = (b.full_name?.split(' ').pop() ?? '').trim();
                if (sortOrder.value === Order.asc) {
                    return lastNameA.localeCompare(lastNameB);
                }
                return lastNameB.localeCompare(lastNameA);
            }

            const aValue = a[sortingColumn.value] ?? '';
            const bValue = b[sortingColumn.value] ?? '';
            if (sortOrder.value === Order.asc) {
                return aValue > bValue ? 1 : (aValue < bValue ? -1 : 0);
            }
            return aValue < bValue ? 1 : (aValue > bValue ? -1 : 0);
        });
    }
});

function sortData(column: keyof ClassStudentInfo) {
    sortClickCount.value++;
    if (sortingColumn.value === column) {
        if (sortClickCount.value % 3 === 0) {
            sortingColumn.value = null;
            sortOrder.value = Order.none
        } else {
            sortOrder.value = sortOrder.value === Order.asc ? Order.desc : Order.asc;
        }
    } else {
        sortingColumn.value = column;
        sortOrder.value = Order.asc;
    }
}
</script>

<template>
    <div class="rounded-lg border-[1px] border-neutral-200 overflow-auto whitespace-pre">
        <table class="table-auto w-full">
            <thead>
                <tr>
                    <th class="sort" v-bind:class="sortingColumn === 'status' ? 'sorting ' + sortOrder : ''"
                        @click="sortData('status')"
                        v-if="classStore.selectedClassDetails?.state != ClassStatus.on_going">
                        {{ t(TEXT.login) }} ({{ loggedInStudents.length }}/{{ students.length }})
                    </th>
                    <th class="text-left sort"
                        v-bind:class="sortingColumn === 'full_name' ? 'sorting ' + sortOrder : ''"
                        @click="sortData('full_name')">{{ t(TEXT.student_name) }}</th>
                    <th class="text-left sort" v-bind:class="sortingColumn === 'username' ? 'sorting ' + sortOrder : ''"
                        @click="sortData('username')">{{ t(TEXT.username) }}</th>
                    <th class="text-left sort" v-bind:class="sortingColumn === 'group_id' ? 'sorting ' + sortOrder : ''"
                        @click="sortData('group_id')">{{ t(TEXT.group) }}</th>
                    <template v-if="classStore.selectedClassDetails?.state === ClassStatus.on_going">
                        <th v-for="session in Session" class="text-left">{{ t(sessionMap[session]) }}</th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="student in students" :key="student?.id">
                    <td v-if="classStore.selectedClassDetails?.state != ClassStatus.on_going">
                        <img :src="checkIcon" class="mx-auto" v-if="student?.status === AttendStatus.attended" />
                    </td>
                    <td>{{ student?.full_name }}</td>
                    <td>{{ student?.username }}</td>
                    <td>{{ classStore.getClassGroupNameById(student?.group_id) }}</td>
                    <template v-if="classStore.selectedClassDetails?.state === ClassStatus.on_going">
                        <td v-for="session in Session">{{ student?.point?.[session] || 0 }}</td>
                    </template>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style lang="postcss" scoped>
thead {
    @apply bg-neutral-200/50
}

tbody {
    @apply bg-white
}

th,
td {
    @apply border-[1px] border-neutral-200 p-2
}

.sort {
    cursor: pointer;

    &:hover {
        @apply bg-neutral-200;
    }

    &::after {
        content: url("data:image/svg+xml,%3Csvg width='7' height='11' viewBox='0 0 7 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.517785 4.92108H6.48272C6.92801 4.92108 7.1646 4.33029 6.8686 3.95669L3.88639 0.194724C3.83802 0.133504 3.77861 0.084491 3.71209 0.050924C3.64557 0.017357 3.57346 0 3.50051 0C3.42756 0 3.35545 0.017357 3.28893 0.050924C3.22241 0.084491 3.163 0.133504 3.11462 0.194724L0.131384 3.95669C-0.164616 4.33029 0.0719774 4.92108 0.517785 4.92108ZM3.11411 10.8053C3.16248 10.8665 3.22189 10.9155 3.28841 10.9491C3.35493 10.9826 3.42705 11 3.49999 11C3.57294 11 3.64505 10.9826 3.71157 10.9491C3.77809 10.9155 3.8375 10.8665 3.88588 10.8053L6.86808 7.04331C7.1646 6.67029 6.92801 6.0795 6.4822 6.0795H0.517785C0.072494 6.0795 -0.164099 6.67029 0.1319 7.04389L3.11411 10.8053Z' fill='black'/%3E%3C/svg%3E%0A");
        margin-left: 5px;
    }
}


.sort.sorting.asc::after {
    content: url("data:image/svg+xml,%3Csvg width='7' height='11' viewBox='0 0 7 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3C!-- Upward Arrow --%3E%3Cpath d='M0.517785 4.92108H6.48272C6.92801 4.92108 7.1646 4.33029 6.8686 3.95669L3.88639 0.194724C3.83802 0.133504 3.77861 0.084491 3.71209 0.050924C3.64557 0.017357 3.57346 0 3.50051 0C3.42756 0 3.35545 0.017357 3.28893 0.050924C3.22241 0.084491 3.163 0.133504 3.11462 0.194724L0.131384 3.95669C-0.164616 4.33029 0.0719774 4.92108 0.517785 4.92108Z' fill='black'/%3E%3C!-- Downward Arrow --%3E%3Cpath d='M3.11411 10.8053C3.16248 10.8665 3.22189 10.9155 3.28841 10.9491C3.35493 10.9826 3.42705 11 3.49999 11C3.57294 11 3.64505 10.9826 3.71157 10.9491C3.77809 10.9155 3.8375 10.8665 3.88588 10.8053L6.86808 7.04331C7.1646 6.67029 6.92801 6.0795 6.4822 6.0795H0.517785C0.072494 6.0795 -0.164099 6.67029 0.1319 7.04389L3.11411 10.8053Z' fill='%23DDDDDD'/%3E%3C/svg%3E");
}

.sort.sorting.desc::after {
    content: url("data:image/svg+xml,%3Csvg width='7' height='11' viewBox='0 0 7 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3C!-- Upward Arrow --%3E%3Cpath d='M0.517785 4.92108H6.48272C6.92801 4.92108 7.1646 4.33029 6.8686 3.95669L3.88639 0.194724C3.83802 0.133504 3.77861 0.084491 3.71209 0.050924C3.64557 0.017357 3.57346 0 3.50051 0C3.42756 0 3.35545 0.017357 3.28893 0.050924C3.22241 0.084491 3.163 0.133504 3.11462 0.194724L0.131384 3.95669C-0.164616 4.33029 0.0719774 4.92108 0.517785 4.92108Z' fill='%23DDDDDD'/%3E%3C!-- Downward Arrow --%3E%3Cpath d='M3.11411 10.8053C3.16248 10.8665 3.22189 10.9155 3.28841 10.9491C3.35493 10.9826 3.42705 11 3.49999 11C3.57294 11 3.64505 10.9826 3.71157 10.9491C3.77809 10.9155 3.8375 10.8665 3.88588 10.8053L6.86808 7.04331C7.1646 6.67029 6.92801 6.0795 6.4822 6.0795H0.517785C0.072494 6.0795 -0.164099 6.67029 0.1319 7.04389L3.11411 10.8053Z' fill='black'/%3E%3C/svg%3E");
}
</style>